import {
  Sheet as ShadSheet,
  SheetContent as ShadSheetContent,
  SheetHeader as ShadSheetHeader,
  SheetTitle as ShadSheetTitle,
} from "@/components/ui/sheet"
import Button from "@/components/button"
import Bottombar from "@/components/bottombar"
import Select, { SelectOption } from "@/components/select"
import { useEffect, useState } from "react"
import { SuggestedEmployee } from "@/services/ipqc"
import { Icon } from "@/icons/icon"
import { cn } from "@/lib/utils"

export interface PendingItem {
  label: string
  subItems?: string[]
}

export type EndShiftSheetProps = {
  open: boolean
  title?: string
  description?: string
  pendingDescription?: string
  pendingItems?: PendingItem[]
  employees?: SuggestedEmployee[]
  onOpenChange?: (open: boolean) => void
  onClickConfirm?: (employeeCode: string) => void | Promise<void>
  onClickBack?: () => void | Promise<void>
}

export default function EndShiftSheet({
  open,
  title = "End Shift",
  description = "You are making the end of your shift for today.",
  pendingDescription = "You are making the end of your shift for today. Please select a reason below",
  pendingItems,
  employees,
  onOpenChange,
  onClickConfirm,
  onClickBack,
}: EndShiftSheetProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([])
  const [handoverCode, setHandoverCode] = useState("")

  useEffect(() => {
    if (!employees) {
      return
    }

    setEmployeeOptions(
      employees.map((item) => ({
        label: item.employeeName,
        value: item.employeeCode,
      }))
    )
  }, [employees])

  useEffect(() => {
    if (!open) {
      setHandoverCode("")
    }
  }, [open])

  async function handleConfirm() {
    if (onClickConfirm) {
      try {
        setIsLoading(true)
        await onClickConfirm(handoverCode)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <ShadSheet open={open} onOpenChange={onOpenChange}>
      <ShadSheetContent
        side="bottom"
        className="shadow-drop-shadow mx-auto rounded-t-lg"
      >
        <ShadSheetHeader className="pb-0 sticky top-0 bg-white">
          <ShadSheetTitle className="text-xl font-semibold flex flex-col gap-5">
            <div className="h-13 w-13 flex items-center justify-center rounded-full bg-backdrop">
              <Icon name="History" className="h-6 w-auto" />
            </div>

            <div>{title}</div>
          </ShadSheetTitle>
        </ShadSheetHeader>

        <div className="font-normal pt-4">
          {pendingItems?.length ? (
            <div className="px-4">
              <div>
                {!!employeeOptions.length
                  ? "Handover is required for sheets with pending readings."
                  : pendingDescription}
              </div>

              <div className="mt-8">
                {!!employeeOptions.length
                  ? "The following pending items will be handed over to the next person."
                  : "The following pending items will be carried over to the next person."}
                <ol className="mt-4 list-decimal pl-4.5">
                  {pendingItems.map((item) => (
                    <li key={item.label} className="mt-3 first:mt-0">
                      <span className="font-medium">{item.label}</span>

                      {!!item.subItems?.length && (
                        <ul className="mt-1 list-disc pl-4.5">
                          {item.subItems.map((sub) => (
                            <li key={sub}>{sub}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ) : (
            <div className="pb-4 px-4">{description}</div>
          )}

          {!!employeeOptions.length && (
            <div className="mt-8 py-4 px-4 sticky bottom-19 bg-white shadow-drop-shadow">
              <Select
                label="Select Employee for Handover"
                options={employeeOptions}
                showValueInSelected
                onChange={setHandoverCode}
              />
            </div>
          )}
        </div>

        <Bottombar
          className={cn(
            "flex gap-4 bg-white sticky bottom-0",
            pendingItems?.length && "shadow-none"
          )}
        >
          <Button variant="outline" onClick={onClickBack}>
            Back
          </Button>
          <Button
            className="flex-1"
            variant="default"
            isLoading={isLoading}
            disabled={!handoverCode && !!employeeOptions.length}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Bottombar>
      </ShadSheetContent>
    </ShadSheet>
  )
}
